<template>
  <section class="fiat-account-creation-wrapper">
    <h1 class="page-title">Create FIAT Account</h1>
    <button @click="goBack()" to="" class="btn back" v-if="!shouldShowReview">
      <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24">
        <path fill="none" d="M0 0h24v24H0z" />
        <path fill="#505050" d="M20 11H7.83l5.59-5.59L12 4l-8 8 8 8 1.41-1.41L7.83 13H20v-2z" />
      </svg>
      Back
    </button>
    <button class="btn icon-btn back" v-if="shouldShowReview && !isCreationLoading && !isCreationSuccessful" @click="shouldShowReview = false">Edit</button>
    <h2>{{ isBeneficiary ? "Create new Beneficiary" : "Create new Sender" }}</h2>
    <!-- BANNERS -->
    <ul class="banner errors" v-if="creationError.length > 0" id="fiat-payout-error-banner">
      <li>Your submission contained some errors :</li>
      <li v-for="error in creationError" :key="error.errorCode">{{ `${error.path} ${error.message}` }}</li>
    </ul>
    <p class="banner warning" v-if="!hasRights">
      This merchant doesn't have the rights to create such account (Restricted to self).
      Please contact our <a href="mailto:support@triple-a.io?subject=Help to create a Fiat Account">support team.</a>
    </p>
    <p class="banner success" v-else-if="isCreationSuccessful && (!kycForm.isUploadSuccessful && !isBeneficiary && accountForm.type === accountTypes.INDIVIDUAL)">
      The account was created successfully but the KYC upload failed. Please try to upload your documents again via the <router-link to="/manage-fiat-accounts">Manage fiat acounts page</router-link>.
    </p>
    <p class="banner success" v-else-if="isCreationSuccessful">
      Account created successfully.
      {{ isBeneficiary ? 'You can now use this account as Beneficiary.' :
      (accountForm.type === accountTypes.CORPORATE ? 'You can now use this account as Sender.' :
        'Our KYC team will now process your application and update your KYC status. Stay tuned !') }}
      <router-link to="/create-fiat-payout">Go to Fiat Payouts</router-link> or <router-link to="/manage-fiat-accounts">Manage your Fiat accounts</router-link>.
    </p>
    <!-- Beneficiary forms -->
    <div v-if="isBeneficiary">
      <form v-if="accountForm.isActive">
        <fieldset class="vital-infos" v-if="!shouldShowReview">
          <legend>Step 1 of 3 : Destination information</legend>
          <label>
            Account type
            <vs-select v-model="accountForm.type" required autocomplete>
              <vs-select-item :key="index" :value="type" :text="type.display" v-for="(type, index) in accountTypes" />
            </vs-select>
          </label>
          <label>Destination Country <span class="req">*</span>
            <vs-select v-model="accountForm.country" autocomplete @change="handleCriteriaChange">
              <vs-select-item v-for="item in accountForm.enabledCountryList" :key="'res_' + item.code" :value="item" :text="item.name" />
            </vs-select>
          </label>
          <label>Destination Currency <span class="req">*</span>
            <vs-select v-model="accountForm.currency" autocomplete :disabled="!accountForm.country" @change="handleCriteriaChange">
              <vs-select-item v-for="cur in accountForm.currencyList" :key="`${cur.currency}_${cur.network}`" :value="cur" :text="`${cur.currency} - ${cur.network}`" />
            </vs-select>
          </label>
          <p class="banner" v-if="accountForm.currency && accountForm.currency.currency && (accountForm.currency.limits_per_transaction.individual || accountForm.currency.limits_per_transaction.company)">
            The maximum transaction limit is <span>{{ transactionLimit }}</span> for this route. If you are planning on sending more than this limit, please select another route or contact our <a href="mailto:support@triple-a.io?subject=Help to create a Fiat Account">support team.</a>
          </p>
          <p class="banner" v-if="accountForm.country && accountForm.currencyList.length < 1">
            No currencies available for this country (404).
            Please select a supported country, or contact our <a href="mailto:support@triple-a.io?subject=Help to create a Fiat Account">support team.</a> </p>
        </fieldset>
      </form>
      <fiat-account-form-factory v-if="areAllParametersSelected"
        :currency="accountForm.currency"
        :country="accountForm.country"
        :accType="accountForm.type"
        :isBenef="isBeneficiary"
        :requiredFields="accountForm.requiredFields"
        :isSubmitRequested="isSubmittingAccount"
        :errors="accountForm.errors"
        :shouldShowReview="shouldShowReview"
        @submitted="onDynamicFormSubmit" />

      <!-- Destination account & review-->
      <destination-account-form v-if="isBeneficiary && areAllParametersSelected"
        :requiredFields="accountForm.requiredFields ? accountForm.requiredFields.destination_account : []"
        :bankList="bankList"
        :isBankListLoading="isBankListLoading"
        :hasBeenSubmitted="isSubmittingPaymentDetails"
        :shouldShowReview="shouldShowReview"
        :errors="paymentDetailsForm.errors"
        @submitted="handleDestinationAccountSubmission"
        @searchBankParam="setBankList" />
    </div>
    <!-- Sender form -->
    <section v-else-if="!isBeneficiary">
      <form v-if="!shouldShowReview" @submit.prevent="">
        <!-- Step 1 : Type selection -->
        <fieldset>
          <legend>Step 1 of 3: Account type</legend>
          <label>
            Account type
            <vs-select v-model="accountForm.type" required autocomplete>
              <vs-select-item :key="index" :value="type" :text="type.display" v-for="(type, index) in accountTypes" />
            </vs-select>
          </label>
        </fieldset>
        <!-- Step 2 : Particulars INDIVIDUAL -->
        <fieldset v-if="accountForm.type === accountTypes.INDIVIDUAL">
          <legend>Step 2 of 3: Individual Particulars</legend>
          <label>First Name <span class="req">*</span>
            <vs-input type="text" v-model.trim="individualForm.firstName"
              :danger="individualForm.errors.firstName"
              danger-text="Please input a valid name (no special characters)"></vs-input>
          </label>
          <label>Last Name <span class="req">*</span>
            <vs-input type="text" v-model.trim="individualForm.lastName"
              :danger="individualForm.errors.lastName"
              danger-text="Please input a valid name (no special characters)"></vs-input>
          </label>
          <label class="small">
            Gender
            <vs-select v-model="individualForm.gender" autocomplete :danger="individualForm.errors.gender" danger-text="Please select a gender">
              <vs-select-item key="1" value="female" text="Female" />
              <vs-select-item key="2" value="male" text="Male" />
            </vs-select>
          </label>
          <label class="small">
            Date of Birth
            <vs-input type="date" :max="today" v-model="individualForm.birthdate"
              :danger="individualForm.errors.birthdate"
              danger-text="Please input a valid date"></vs-input>
          </label>
          <label class="full">
            Your internal reference
            <span class="tooltip">
              <img
                src="../../assets/icons/help.svg"
                alt="more info on internal reference" />
              <span class="tooltip-text">
                <strong>Internal reference</strong>
                <br>
                You can provide your own reference to easily find this transaction in the future.
              </span>
            </span>
            <vs-input v-model="individualForm.referenceId" type="text" max="100"></vs-input>
          </label>
          <label>
            Email address
            <span class="tooltip">
              <img
                src="../../assets/icons/help.svg"
                alt="more info on email" />
              <span class="tooltip-text">
                <strong>Email</strong>
                <br>
                Email is required for compliance purposes only. We will not send any emails to this address.
              </span>
            </span>
            <vs-input type="email" icon="email" v-model.trim="individualForm.email"
              :danger="individualForm.errors.email"
              danger-text="Please input a valid email"></vs-input>
          </label>
          <label class="small">
            Contact number (Mobile) <span class="req">*</span>
            <span class="tooltip">
              <img
                src="../../assets/icons/help.svg"
                alt="more info on email" />
              <span class="tooltip-text">
                <strong>Contact number</strong>
                <br>
                Mobile number is required for compliance purposes only. We will not contact this number.
              </span>
            </span>
            <vs-input icon="phone" v-model.trim="individualForm.phone"
              placeholder="+65 12345678"
              description-text="Please put your country indicator (+)"
              :danger="individualForm.errors.phone"
              danger-text="Please input a valid phone number, including + and your country indicator"></vs-input>
          </label>
          <label>
            Occupation
            <vs-select v-model="individualForm.occupation" autocomplete>
              <vs-select-item :key="occupation.val" :value="occupation" :text="occupation.display" v-for="occupation in Object.values(occupationList)" />
            </vs-select>
          </label>
          <label>Country of residence <span class="req">*</span>
            <vs-select v-model="individualForm.residenceCountry" autocomplete
              :danger="individualForm.errors.residenceCountry"
              danger-text="Please select a country">
              <vs-select-item v-for="item in countryList" :key="'res_' + item.code" :value="item" :text="item.name" />
            </vs-select>
          </label>
          <label>
            Address <span class="req">*</span>
            <vs-input v-model.trim="individualForm.address"
              :danger="individualForm.errors.address"
              danger-text="Please input a valid address"></vs-input>
          </label>
          <label class="small">
            Zip code <span class="req">*</span>
            <vs-input v-model.trim="individualForm.zip"
              :danger="individualForm.errors.zip"
              danger-text="Please input a valid zip code"></vs-input>
          </label>
          <label class="small">
            City <span class="req">*</span>
            <vs-input v-model.trim="individualForm.city"
              :danger="individualForm.errors.city"
              danger-text="Please input a valid city"></vs-input>
          </label>
          <label class="small">
            Province / state <span class="req">*</span>
            <vs-input v-model.trim="individualForm.state"
              :danger="individualForm.errors.state"
              danger-text="Please input a valid state"></vs-input>
          </label>

          <p>Identification</p>
          <div class="identification">
            <label class="small">
              ID type <span class="req">*</span>
              <vs-select v-model="individualForm.idType" autocomplete
                :danger="individualForm.errors.idType"
                danger-text="Please select an ID type">
                <vs-select-item v-for="item in idTypeList" :key="item.value" :value="item" :text="item.display" />
              </vs-select>
            </label>
            <label class="small">
              ID number <span class="req">*</span>
              <vs-input v-model.trim="individualForm.idNumber"
                :danger="individualForm.errors.idNumber"
                danger-text="Please input a valid ID number"></vs-input>
            </label>
            <label class="small">
              Nationality
              <vs-select v-model="individualForm.nationality" autocomplete>
                <vs-select-item v-for="item in countryList" :key="'id_' + item.code + '_nat'" :value="item" :text="item.name" />
              </vs-select>
            </label>
            <label class="small">
              Issuing country
              <vs-select v-model="individualForm.idIssuingCountry" autocomplete>
                <vs-select-item v-for="item in countryList" :key="'id_' + item.code + '_iss'" :value="item" :text="item.name" />
              </vs-select>
            </label>
            <label class="small">
              Issue Date
              <vs-input type="date" :max="today" v-model="individualForm.idIssueDate"></vs-input>
            </label>
            <label class="small">
              Expiry Date
              <vs-input type="date" v-model="individualForm.idExpDate"></vs-input>
            </label>
          </div>
        </fieldset>
        <!-- Step 2 : Particulars CORPORATE -->
        <fieldset v-else-if="accountForm.type === accountTypes.CORPORATE">
          <legend>Step 2 of 3: Corporate Particulars</legend>
          <label class="full">
            Your Internal Reference
            <vs-input v-model="corporateForm.internalRef"></vs-input>
          </label>
          <!-- <label>
          Email address <span class="req">*</span>
          <span class="tooltip">
            <img
              src="../../assets/icons/help.svg"
              alt="more info on email" />
            <span class="tooltip-text">
              <strong>Email</strong>
              <br>
              Email is required for compliance purposes only. We will not send any emails to this address.
            </span>
          </span>
          <vs-input type="email" icon="email"
            v-model="corporateForm.email"
            :danger="corporateForm.errors.email"
            danger-text="Please input a valid email"></vs-input>
        </label> -->
          <p>Registration</p>
          <label>
            Registered Name <span class="req">*</span>
            <vs-input v-model="corporateForm.registration.nameRegistered"
              :danger="corporateForm.errors.nameRegistered"
              danger-text="Please input a valid name">
            </vs-input>
          </label>
          <label>
            Trading Name
            <vs-input v-model="corporateForm.registration.tradeName"></vs-input>
          </label>
          <!-- <label class="small">
          License Type <span class="req">*</span>
          <vs-input v-model="corporateForm.registration.licenseType" placeholder="ex: trade license"
            :danger="corporateForm.errors.licenseType"
            danger-text="Please input a valid type">
          </vs-input>
        </label> -->
          <label>
            Registration Number <span class="req" v-if="!isBeneficiary">*</span>
            <vs-input v-model="corporateForm.registration.registrationNumber"
              :danger="corporateForm.errors.registrationNumber"
              danger-text="Please input a valid number"></vs-input>
          </label>
          <label class="small" v-if="!isBeneficiary">
            Date of Incorporation
            <vs-input type="date" :max="today" v-model="corporateForm.registration.incorporationDate"></vs-input>
          </label>
          <label v-if="!isBeneficiary">
            Country of Incorporation <span class="req">*</span>
            <vs-select v-model="corporateForm.registration.incorporationCountry" autocomplete
              :danger="corporateForm.errors.incorporationCountry"
              danger-text="Please select a country">
              <vs-select-item v-for="item in countryList" :key="'addr_' + item.code" :value="item" :text="item.name" />
            </vs-select>
          </label>
          <label>
            Contact number (Mobile)
            <span class="tooltip">
              <img
                src="../../assets/icons/help.svg"
                alt="more info on email" />
              <span class="tooltip-text">
                <strong>Phone number</strong>
                <br>
                We will not contact this number. This phone number will be stored for compliance purposes only.
              </span>
            </span>
            <vs-input icon-no-border icon="phone" v-model="corporateForm.phone"
              :danger="corporateForm.errors.phone"
              danger-text="Please input a valid phone number, including your country indicator">
            </vs-input>
          </label>
          <label v-if="!isBeneficiary">
            Business Nature
            <vs-select v-model="corporateForm.registration.businessNature" autocomplete>
              <vs-select-item v-for="item in Object.values(businessNatureList)" :key="item.val" :value="item" :text="item.display" />
            </vs-select>
          </label>
          <label class="three-quarter" v-if="!isBeneficiary">
            Business incorporation document (ex : Certificate of incorporation)
            <input type="file" accept="image/jpeg,application/pdf,image/png" name="file" @change="onBusinessIncFileChange" />
            <span class="error" v-if="corporateForm.errors.businessIncDoc">Please upload a valid supporting document (max 5MB)</span>
          </label>
          <p>Address</p>
          <label>
            Address <span class="req">*</span>
            <vs-input v-model="corporateForm.address.address"
              :danger="corporateForm.errors.address"
              danger-text="Please input a valid address"></vs-input>
          </label>
          <label class="small">
            Zip code <span class="req">*</span>
            <vs-input v-model="corporateForm.address.zip"
              :danger="corporateForm.errors.zip"
              danger-text="Please input a valid zip code"></vs-input>
          </label>
          <label class="small">
            Province / State <span class="req">*</span>
            <vs-input v-model="corporateForm.address.state"
              :danger="corporateForm.errors.state"
              danger-text="Please input a valid state"></vs-input>
          </label>
          <label class="small">
            City <span class="req">*</span>
            <vs-input v-model="corporateForm.address.city"
              :danger="corporateForm.errors.city"
              danger-text="Please input a valid city"></vs-input>
          </label>
          <label>
            Country <span class="req">*</span>
            <vs-select v-model="corporateForm.address.country" autocomplete
              :danger="corporateForm.errors.country"
              danger-text="Please select a country">
              <vs-select-item v-for="item in countryList" :key="'addr_' + item.code" :value="item" :text="item.name" />
            </vs-select>
          </label>

          <p>UBO / Director's Proof Of Identity (POI)</p>
          <div class="company-documents">
            <label class="small">
              Document type
              <vs-select v-model="corporateForm.registration.uboDocType" autocomplete>
                <vs-select-item :key="DOC_TYPES.PASSPORT.value" :value="DOC_TYPES.PASSPORT.value" :text="DOC_TYPES.PASSPORT.display"></vs-select-item>
                <vs-select-item :key="DOC_TYPES.NATIONAL_ID.value" :value="DOC_TYPES.NATIONAL_ID.value" :text="DOC_TYPES.NATIONAL_ID.display"></vs-select-item>
                <vs-select-item :key="DOC_TYPES.DRIVING_LICENSE.value" :value="DOC_TYPES.DRIVING_LICENSE.value" :text="DOC_TYPES.DRIVING_LICENSE.display"></vs-select-item>
              </vs-select>
            </label>
            <label>
              Document
              <input type="file" accept="image/jpeg,application/pdf,image/png" name="file" @change="onUboDocFileChange" />
              <span class="error" v-if="corporateForm.errors.uboDoc">Please input a valid document (max 5MB)</span>
            </label>
          </div>

        </fieldset>
      </form>
      <!-- REVIEW -->
      <fiat-account-review
        v-if="shouldShowReview"
        :accountType="accountForm.type"
        :account="isBeneficiary ? accountForm.inputs : {}"
        :paymentDetails="isBeneficiary ? paymentDetailsForm.inputs : {}"
        :individualForm="!isBeneficiary ? individualForm : {}"
        :corporateForm="!isBeneficiary ? corporateForm : {}"
        :isBeneficiary="isBeneficiary" />
      <!-- KYC -->
      <div v-if="shouldShowReview && !isBeneficiary && merchantConfig.kyc_reliance !== 'trust' && accountType === accountTypes.INDIVIDUAL" class="kyc-wrapper">
        <h3>KYC documents</h3>
        <p class="banner info">Sender accounts need to be KYC approved before you can use it. Please upload your KYC documents here and keep track of it's approval on the <router-link to="/manage-fiat-accounts">Manage fiat accounts page</router-link>.</p>
        <FiatKycForm
          :confirmOrder="kycForm.isOrderToConfirm"
          :accountId="createdAccount.id"
          :shouldResetKycForm="shouldResetKycForm"
          @fileSelected="kycForm.kycFileInput = true"
          @fileDeleted="kycForm.kycFileInput = false"
          @processSuccess="kycForm.isUploadSuccessful = true"
          @processFail="kycForm.isUploadSuccessful = false">
        </FiatKycForm>
        <p v-if="kycForm.errors.kyc" class="error">Please upload a valid file for KYC.</p>
      </div>
    </section>

    <div id="account-creation-loader" class="vs-con-loading__container"></div>

    <button class="btn confirm" @click="confirmBtnHandler" v-if="((isBeneficiary && areAllParametersSelected) || !isBeneficiary) && !isCreationLoading && !isCreationSuccessful && hasRights">{{ shouldShowReview ? "Confirm and Create" : "Next" }}</button>
    <p v-if="hasErrors && !shouldShowReview" class="error">Your submission contains some errors. Please double check and try again.</p>
  </section>
</template>

<script>
import { PAYOUT_ACCOUNT_TYPES, FIAT_ID_TYPE, PAYOUT_DOC_TYPES, FIAT_ROUTING_TYPES, FIAT_BANK_ACCOUNT_TYPES, FIAT_BANK_ACCOUNT_FORMATS, FIAT_ACCOUNT_BUSINESS_NATURE, FIAT_ACCOUNT_OCCUPATION } from '../../constants';
import FiatAccountFormFactory from '../../factories/FiatAccountFormFactory.vue';
import DestinationAccountForm from '../../molecules/DestinationAccountForm.vue';
import FiatAccountReview from '../../molecules/FiatAccountReview.vue';
import FiatKycForm from '../../molecules/FiatKycForm.vue';

export default {
  props: {
    isBeneficiary: {
      type: Boolean,
      default: true
    }
  },
  data() {
    return {
      accountTypes: PAYOUT_ACCOUNT_TYPES,
      MAX_FILE_SIZE: 5 * 1024 * 1024,
      today: new Date().toISOString().split('T')[0],
      shouldShowReview: false,
      hasErrors: false,
      individualForm: {
        firstName: '',
        lastName: '',
        gender: '',
        birthdate: '',
        referenceId: '',
        email: '',
        phone: '',
        residenceCountry: '',
        address: '',
        zip: '',
        city: '',
        state: '',
        idIssuingCountry: '',
        nationality: '',
        idType: '',
        idNumber: '',
        idIssueDate: '',
        idExpDate: '',
        errors: {
          firstName: false,
          lastname: false,
          birthdate: false,
          gender: false,
          phone: false,
          residenceCountry: false,
          idType: false,
          idNumber: false,
          email: false,
          address: false,
          zip: false,
          city: false,
          state: false,
        }
      },
      corporateForm: {
        internalRef: '',
        email: '',
        phone: '',
        registration: {
          nameRegistered: '',
          tradeName: '',
          licenseType: 'Trade license',
          registrationNumber: '',
          incorporationDate: '',
          incorporationCountry: '',
          uboDocType: PAYOUT_DOC_TYPES.PASSPORT.value,
          uboDoc: null,
          businessNature: '',
          businessIncDoc: null,
        },
        address: {
          country: undefined,
          city: '',
          state: '',
          zip: '',
          address: '',
        },
        errors: {
          email: false,
          phone: false,
          nameRegistered: false,
          uboDoc: false,
          businessIncDoc: false,
          address: false,
          city: false,
          zip: false,
          country: false,
        }
      },
      kycForm: {
        isOrderToConfirm: false,
        kycFileInput: null,
        isUploadSuccessful: false,
        errors: {
          kyc: false
        }
      },
      merchantConfig: {
        is_restrict_to_self: '',
        kyc_reliance: '',
        name: '',
        restricted_sender_ids: '',
        restricted_sender_type: '',
        whitelist_domains: '',
      },
      createdAccount: {
        id: '',
      },

      // flags
      hasRights: false,
      isCreationLoading: false,
      isCreationSuccessful: false,
      creationError: [],
      isBankListLoading: false,
      shouldResetKycForm: false,

      // Options
      countryList: [],
      bankList: [],
      idTypeList: FIAT_ID_TYPE,
      occupationList: FIAT_ACCOUNT_OCCUPATION,
      businessNatureList: FIAT_ACCOUNT_BUSINESS_NATURE,
      paymentAccountFormats: FIAT_BANK_ACCOUNT_FORMATS,
      paymentAccountTypetest: [],
      paymentAccountType: FIAT_BANK_ACCOUNT_TYPES,
      routingTypeList: FIAT_ROUTING_TYPES,
      DOC_TYPES: PAYOUT_DOC_TYPES,

      // Dynamic form
      accountForm: {
        isActive: true,
        country: '',
        currency: '',
        type: PAYOUT_ACCOUNT_TYPES.CORPORATE,
        inputs: {},
        errors: {},
        enabledCountryList: [],
        requiredFields: {},
        currencyList: [],
      },
      // payment details 
      paymentDetailsForm: {
        errors: {},
        inputs: {
          type: '',
          receiving_institution_id: '', // bank id
          currency: '', // bank currency
          country_code: '', // bank country
          bank_name: '',
          account: {
            account_number: '',
            account_type: '',
            bank_account_type: '',
            routing_type: '',
            routing_code: '',
          }
        },
      },

      isSubmittingAccount: false,
      isSubmittingPaymentDetails: false,
    };
  },
  mounted() {
    this.initPage();
  },
  computed: {
    getIncorporationCountry() {
      return this.corporateForm.registration.incorporationCountry;
    },
    activeMerchantKey() {
      return this.$store.getters.activeMerchantKeyGetter;
    },
    routingType() {
      return this.paymentDetailsForm.routingType;
    },
    accountType() {
      return this.accountForm.type;
    },
    transactionLimit() {
      const amt = this.accountForm.type === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL ? this.accountForm.currency.limits_per_transaction.individual : this.accountForm.currency.limits_per_transaction.company;
      const cur = this.accountForm.currency.currency;
      return `${this.formatFiatPrice(amt)} ${cur}`;
    },
    areAllParametersSelected() {
      return !!this.accountForm.type && !!this.accountForm.country && !!this.accountForm.currency;
    },
    accountCurrency() {
      return this.accountForm.currency;
    },
    accountCountry() {
      return this.accountForm.country;
    },
  },
  methods: {
    async initPage() {
      this.resetData();
      this.setDefaultValues();
      this.getCountryList();
      this.setEnabledCountryList();
      this.getFiatMerchantConfig();
      this.checkRights();
    },
    createIndiv() {
      this.creationError = [];
      if (this.createdAccount.id && this.isBeneficiary) {
        this.getRequestToken().then(token => {
          this.createDestinationAccount(token, this.createdAccount.id);
        }).catch(e => {
          console.error(e.response, e.response.data.errors);
          this.creationErrorsHandler(e);
        });
        return;
      }
      this.isCreationSuccessful = false;
      this.isCreationLoading = true;
      const payload = this.buildAccPayload();
      this.getRequestToken().then(token => {
        this.$store.dispatch('createFiatIndividualAccount', { token, payload }).then(res => {
          this.createdAccount = res.data;
          this.accountForm.id = res.data.id;
          if (this.isBeneficiary) {
            this.createDestinationAccount(token, res.data.id);
          } else {
            if (this.merchantConfig.kyc_reliance === 'trust') {
              // Merchant is trusted, no need KYC
              this.kycForm.isUploadSuccessful = true;
            } else {
              this.kycForm.isOrderToConfirm = true;
            }
            // We continue as KYC should be done in the background
            this.isCreationLoading = false;
            this.creationError = [];
            this.isCreationSuccessful = true;
          }
        }).catch(e => {
          console.error(e.response, e.response.data.errors);
          this.creationErrorsHandler(e);
        });
      }).catch(e => {
        this.creationErrorsHandler(e);
      });
    },
    createCompany() {
      this.creationError = [];
      if (this.createdAccount.id && this.isBeneficiary) {
        // To not create a new account if onl the destination account is wrong
        this.getRequestToken().then(token => {
          this.createDestinationAccount(token, this.createdAccount.id);

        }).catch(e => {
          console.error(e.response, e.response.data.errors);
          this.creationErrorsHandler(e);
        });
        return;
      }
      this.isCreationSuccessful = false;
      this.isCreationLoading = true;
      // hack for brdgx, since country of incorporation is necessary for them, only beneficiaries
      if (this.isBeneficiary && !this.corporateForm.registration.incorporationCountry) {
        this.corporateForm.registration.incorporationCountry = this.corporateForm.address.country;
      }
      const payload = this.buildAccPayload();
      this.getRequestToken().then(token => {
        this.$store.dispatch('createFiatCorporateAccount', { token, payload }).then(res => {
          this.createdAccount = res.data;
          this.accountForm.id = res.data.id;
          if (this.isBeneficiary) {
            this.createDestinationAccount(token, res.data.id);
          }
          else {
            this.isCreationLoading = false;
            this.isCreationSuccessful = true;
            this.creationError = [];
          }
          this.uploadDocuments(token, res.data.id, true);
        }).catch(e => {
          this.creationErrorsHandler(e);
        });
      }).catch(e => {
        this.creationErrorsHandler(e);
      });
    },
    createDestinationAccount(token, ownerId) {
      const account = this.buildPaymentDetailsPayload(ownerId);
      this.$store.dispatch('createFiatDestinationAccount', { token, account }).then(() => {
        this.isCreationSuccessful = true;
        this.isCreationLoading = false;
        this.scrollToTop();
      }).catch((e) => {
        console.error(e);
        this.isCreationSuccessful = false;
        this.isCreationLoading = false;
        e.response.data.errors.forEach(err => {
          this.creationError.push(this.formatErrorMessage(err));
        });
        this.creationError.push({
          path: '',
          message: 'Please check your payment details and try again.'
        });
        this.scrollToTop();
      });
    },
    uploadDocuments(token, ownerId, isCompany) {
      // business incorporation document
      const data = {
        entityId: ownerId,
        file: this.corporateForm.registration.businessIncDoc,
        isCompany: isCompany,
        document_type: PAYOUT_DOC_TYPES.BUSINESS_INCORPORATION.value,
      };
      if (this.corporateForm.registration.businessIncDoc) {
        this.$store.dispatch('uploadDocForFiatAccount', { token: token, data: data });
      }
      // ubo document
      data.document_type = this.corporateForm.registration.uboDocType;
      data.file = this.corporateForm.registration.uboDoc;
      if (this.corporateForm.registration.uboDoc) {
        this.$store.dispatch('uploadDocForFiatAccount', { token: token, data: data });
      }
    },
    checkRights() {
      const hasFiatPayoutRights = this.$store.getters.activeMerchantGetter.is_fiat_payout;
      const hasSenderCreationRights = (!this.merchantConfig.is_restrict_to_self && !!this.merchantConfig.restricted_sender_id) || !this.merchantConfig.is_restrict_to_self;
      this.hasRights = hasFiatPayoutRights && (this.isBeneficiary || hasSenderCreationRights);
    },

    /*************************** HANDLERS ****************************/
    confirmBtnHandler() {
      this.hasErrors = false;
      if (!this.shouldShowReview) {
        this.triggerFormsSubmissions(); // gather all the inputs
        if (this.isFormValid()) { // validation of all forms
          this.shouldShowReview = true;
          this.resetAllErrors();
        } else {
          this.hasErrors = true;
          this.shouldShowReview = false;
        }
      }
      else {
        // make request to create account
        if (this.accountForm.type.value === this.accountTypes.INDIVIDUAL.value) {
          if (this.isBeneficiary
            || this.merchantConfig.kyc_reliance === 'trust'
            || (!this.isBeneficiary && this.isKycFormValid())) {
            this.createIndiv();
          } else {
            this.hasErrors = true;
          }
        }
        else {
          this.createCompany();
        }
      }
    },

    goBack() {
      this.resetAllErrors();
      window.history.go(-1);
    },

    triggerFormsSubmissions() {
      this.resetAllErrors();
      this.isSubmittingAccount = true;
      // payment details form will be triggered after the account inputs are received
      // So that we can directly populate the account id, name
    },

    onDynamicFormSubmit(form) {
      this.isSubmittingAccount = false;
      this.accountForm.inputs = {};
      for (const val of form) {
        if (val.value) {
          this.accountForm.inputs[val.name] = val;
        }
      }
      this.setAccountName();

      // Trigger the payment details form
      if (this.isBeneficiary) {
        this.isSubmittingPaymentDetails = true;
        return;
      }
      if (!this.isFormValid()) {
        this.shouldShowReview = false;
        return;
      }
      this.shouldShowReview = true;
    },

    handleDestinationAccountSubmission(form) {
      this.isSubmittingPaymentDetails = false;
      this.storePaymentDetailsInputs(form); // stores inputs
      if (this.isPaymentDetailsValid()) { // validation of all forms
        this.shouldShowReview = true;
      } else {
        this.shouldShowReview = false;
        this.hasErrors = true;
      }
    },


    onBusinessIncFileChange(e) {
      this.corporateForm.errors.businessIncDoc = false;
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) {
        this.corporateForm.registration.businessIncDoc = null;
        return;
      } else if (files[0].size >= this.MAX_FILE_SIZE) {
        this.corporateForm.registration.businessIncDoc = null;
        this.corporateForm.errors.businessIncDoc = true;
        return;
      } else {
        this.corporateForm.registration.businessIncDoc = files[0];
      }
    },

    onUboDocFileChange(e) {
      this.corporateForm.errors.uboDoc = false;
      const files = e.target.files || e.dataTransfer.files;
      if (!files.length) {
        this.corporateForm.registration.uboDoc = null;
        return;
      } else if (files[0].size >= this.MAX_FILE_SIZE) {
        this.corporateForm.registration.uboDoc = null;
        this.corporateForm.errors.uboDoc = true;
        return;
      } else {
        this.corporateForm.registration.uboDoc = files[0];
      }
    },

    creationErrorsHandler(e) {
      this.creationError = [];
      this.isCreationSuccessful = false;
      this.isCreationLoading = false;
      if (e.response.data.errors) {
        e.response.data.errors.forEach(err => {
          this.creationError.push(this.formatErrorMessage(err));
        });
      }
      else {
        if (e.response.status === 500) {
          this.creationError.push({
            path: e.response.status,
            message: 'Something went wrong. Please try again later.'
          });
        }
        else {
          this.creationError.push({
            path: e.response.status,
            message: e.response.data.message
          });
        }
      }
    },

    handleCriteriaChange() {
      if (this.accountForm.country && this.accountForm.currency.currency) {
        this.setBankList();
      }
    },
    /*************************** VALIDATORS ****************************/
    isFormValid() {
      this.resetAllErrors();
      if (this.accountForm.type.value === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value) {
        // INDIVIDUAL account form validation. Payment details are mandatory only for beneficiaries
        return this.isIndividualFormValid() && (!this.isBeneficiary || this.isPaymentDetailsValid());
      }
      else {
        // company account form validation
        return this.isCorporateFormValid() && (!this.isBeneficiary || this.isPaymentDetailsValid());
      }
    },
    isIndividualFormValid() {
      let isValid = true;
      this.resetIndividualFormError();
      if (!this.isBeneficiary) {
        if (!this.individualForm.firstName) {
          this.individualForm.errors.firstName = true;
          isValid = false;
        }
        if (!this.individualForm.lastName) {
          this.individualForm.errors.lastName = true;
          isValid = false;
        }
        if (!this.individualForm.residenceCountry) {
          isValid = false;
          this.individualForm.errors.residenceCountry = true;
        }
        if (!this.individualForm.idType && !this.isBeneficiary) {
          isValid = false;
          this.individualForm.errors.idType = true;
        }
        if (!this.individualForm.idNumber && !this.isBeneficiary) {
          isValid = false;
          this.individualForm.errors.idNumber = true;
        }
        if (!this.individualForm.address) {
          isValid = false;
          this.individualForm.errors.address = true;
        }
        if (!this.individualForm.city) {
          isValid = false;
          this.individualForm.errors.city = true;
        }
        if (!this.individualForm.zip) {
          isValid = false;
          this.individualForm.errors.zip = true;
        }
        if (!this.individualForm.state) {
          isValid = false;
          this.individualForm.errors.state = true;
        }

        return isValid;
      }
      if (Object.values(this.accountForm.inputs).length < 1) {
        return false;
      }
      for (let field of Object.values(this.accountForm.inputs)) {
        if (field.isRequired && !field.value) {
          this.accountForm.errors[field.name] = true;
          isValid = false;
        } else {
          if (field.name === 'email') {
            if (!this.isEmail(field.value)) {
              this.accountForm.errors[field.name] = true;
              isValid = false;
            }
          }
          else if (field.name === 'phone') {
            if (!this.isPhoneNumber(field.value)) {
              this.accountForm.errors[field.name] = true;
              isValid = false;
            }
          }
          else if (field.name === 'birthdate') {
            if (field.value > this.today) {
              this.accountForm.errors[field.name] = true;
              isValid = false;
            }
          }
        }
        return isValid;
      }
    },
    isCorporateFormValid() {
      let isValid = true;
      this.resetCorporateFormErrors();
      if (!this.isBeneficiary) {
        if (!this.corporateForm.registration.nameRegistered) {
          isValid = false;
          this.corporateForm.errors.nameRegistered = true;
        }
        if (this.corporateForm.phone && !this.isPhoneNumber(this.corporateForm.phone)) {
          isValid = false;
          this.corporateForm.errors.phone = true;
        }
        if (!this.isBeneficiary && (!this.corporateForm.registration.incorporationCountry || !this.corporateForm.registration.incorporationCountry.code)) {
          isValid = false;
          this.corporateForm.errors.incorporationCountry = true;
        }
        if (!this.isBeneficiary && !this.corporateForm.registration.licenseType) {
          isValid = false;
          this.corporateForm.errors.licenseType = true;
        }
        if (!this.isBeneficiary && !this.corporateForm.registration.registrationNumber) {
          isValid = false;
          this.corporateForm.errors.registrationNumber = true;
        }
        if (this.corporateForm.registration.businessIncDoc && this.corporateForm.registration.businessIncDoc.size >= this.MAX_FILE_SIZE) {
          isValid = false;
          this.corporateForm.errors.businessIncDoc = true;
        }
        if (this.corporateForm.registration.uboDoc && this.corporateForm.registration.uboDoc.size >= this.MAX_FILE_SIZE) {
          isValid = false;
          this.corporateForm.errors.uboDoc = true;
        }
        if (!this.corporateForm.address.country || !this.corporateForm.address.country.code) {
          isValid = false;
          this.corporateForm.errors.country = true;
        }
        if (!this.corporateForm.address.address) {
          isValid = false;
          this.corporateForm.errors.address = true;
        }
        if (!this.corporateForm.address.city) {
          isValid = false;
          this.corporateForm.errors.city = true;
        }
        if (!this.corporateForm.address.zip) {
          isValid = false;
          this.corporateForm.errors.zip = true;
        }

      } else {
        if (Object.values(this.accountForm.inputs).length < 1) {
          return false;
        }
        for (let field of Object.values(this.accountForm.inputs)) {
          if (field.isRequired && !field.value) {
            this.accountForm.errors[field.name] = true;
            isValid = false;
          } else {
            if (field.name === 'email') {
              if (!this.isEmail(field.value)) {
                this.accountForm.errors[field.name] = true;
                isValid = false;
              }
            }
            else if (field.name === 'phone') {
              if (!this.isPhoneNumber(field.value)) {
                this.accountForm.errors[field.name] = true;
                isValid = false;
              }
            }
          }
        }
      }
      return isValid;
    },
    isPaymentDetailsValid() {
      let isValid = true;
      // Since the check is presence or not, we let the validation at the component HTML level
      // Exception made for the bank and account number
      // If we need to add more errors, we can do the same as the account form and merge the 2 functions
      if (!this.paymentDetailsForm.inputs.bank_name || !this.paymentDetailsForm.inputs.currency) {
        isValid = false;
        this.paymentDetailsForm.errors.bank = true;
      }
      if (!this.paymentDetailsForm.inputs.account.account_number) {
        isValid = false;
        this.paymentDetailsForm.errors.account_number = true;
      }
      return isValid;
    },
    isKycFormValid() {
      let isValid = true;
      if (!this.kycForm.kycFileInput && !this.isBeneficiary) {
        isValid = false;
        this.kycForm.errors.kyc = true;
      }
      return isValid;
    },
    isPhoneNumber(num) {
      return /\+[0-9]+/.test(num.replace(/ |\(|\)/g, ''));
    },
    /********************* HELPERS ***********************/
    buildAccPayload() {
      if (this.isBeneficiary) {
        const payload = {
          'role': 'recipient',
        };
        for (let field of Object.values(this.accountForm.inputs)) {
          payload[field.name] = field.value;
        }
        if (this.accountForm.type.value === PAYOUT_ACCOUNT_TYPES.CORPORATE.value) {
          payload.registration_type = 'Trade license';
        }
        return payload;
      } else {
        return this.buildSenderPayload();
      }
    },
    buildSenderPayload() {
      const payload = {
        'role': 'sender',
        'type': this.accountForm.type.value,
      };
      if (this.accountForm.type.value === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value) {
        payload['last_name'] = this.individualForm.lastName;
        payload['first_name'] = this.individualForm.firstName;
        payload['country_code'] = this.individualForm.residenceCountry.code;
        this.individualForm.idType ? payload['identification_type'] = this.individualForm.idType.value : null;
        this.individualForm.idNumber ? payload['identification_number'] = this.individualForm.idNumber : null;
        this.individualForm.referenceId ? payload['external_id'] = this.individualForm.referenceId : null;
        this.individualForm.email ? payload['email'] = this.individualForm.email : null;
        this.individualForm.state ? payload['province_state'] = this.individualForm.state : null;
        this.individualForm.city ? payload['city'] = this.individualForm.city : null;
        this.individualForm.address ? payload['address'] = this.individualForm.address : null;
        this.individualForm.zip ? payload['zip_code'] = this.individualForm.zip : null;
        this.individualForm.idIssuingCountry ? payload['identification_issuer'] = this.individualForm.idIssuingCountry.name : null;
        this.individualForm.idIssueDate ? payload['identification_issue_date'] = this.individualForm.idIssueDate : null;
        this.individualForm.idExpDate ? payload['identification_expiry_date'] = this.individualForm.idExpDate : null;
        this.individualForm.nationality ? payload['nationality'] = this.individualForm.nationality.code : null;
        this.individualForm.gender ? payload['gender'] = this.individualForm.gender : null;
        this.individualForm.birthdate ? payload['date_of_birth'] = this.individualForm.birthdate : null;
      } else {
        payload['registered_name'] = this.corporateForm.registration.nameRegistered;
        payload['country_code'] = this.corporateForm.address.country.code;
        payload['registration_country'] = this.corporateForm.registration.incorporationCountry.code;
        this.corporateForm.registration.licenseType ? payload['registration_type'] = this.corporateForm.registration.licenseType : {};
        this.corporateForm.registration.registrationNumber ? payload['registration_number'] = this.corporateForm.registration.registrationNumber : {};
        this.corporateForm.internalRef ? payload['external_id'] = this.corporateForm.internalRef : {};
        this.corporateForm.registration.tradeName ? payload['trading_name'] = this.corporateForm.registration.tradeName : {};
        this.corporateForm.registration.incorporationDate ? payload['registration_date'] = this.corporateForm.registration.incorporationDate : {};
        this.corporateForm.phone ? payload['phone_number'] = this.corporateForm.phone : {};
        this.corporateForm.address.address ? payload['address'] = this.corporateForm.address.address : {};
        this.corporateForm.address.zip ? payload['zip_code'] = this.corporateForm.address.zip : {};
        this.corporateForm.address.city ? payload['city'] = this.corporateForm.address.city : {};
      }
      return payload;
    },
    buildPaymentDetailsPayload(owner_id) {
      try {
        const account = this.paymentDetailsForm.inputs;
        account['owner_id'] = owner_id;
        account['account_name'] = this.accountForm.account_name;
        account['account']['network'] = 'network:' + this.accountForm.currency.network;
        return account;
      } catch (e) {
        console.error(e);
        return {};
      }
    },
    storePaymentDetailsInputs(form) {
      this.paymentDetailsForm.inputs.type = form.type.val;
      this.paymentDetailsForm.inputs.receiving_institution_id = form.receiving_institution_id;
      this.paymentDetailsForm.inputs.currency = form.currency;
      this.paymentDetailsForm.inputs.country_code = form.country_code;
      this.paymentDetailsForm.inputs.owner_id = this.accountForm.id;
      this.paymentDetailsForm.inputs.bank_name = form.bank_name;
      this.paymentDetailsForm.inputs.account = {
        account_name: this.accountForm.account_name,
        account_number: form.account.account_number,
        network: this.accountForm.currency.network,
      };
      // optional fields
      if (form.account.account_type) {
        this.paymentDetailsForm.inputs.account['account_type'] = form.account.account_type;
      }
      if (form.account.routing_type) {
        this.paymentDetailsForm.inputs.account['routing_type'] = form.account.routing_type;
      }
      if (form.account.routing_code) {
        this.paymentDetailsForm.inputs.account['routing_code'] = form.account.routing_code;
      }
      if (form.account.bank_account_type) {
        this.paymentDetailsForm.inputs.account['bank_account_type'] = form.account.bank_account_type;
      }
      if (form.account.branch_code) {
        this.paymentDetailsForm.inputs.account['branch_code'] = form.account.branch_code;
      }
    },
    filterCountryList(list) {
      return list.filter(item => (item.services && item.services.bank_account && item.services.bank_account.length > 0));
    },
    getCountryList() {
      this.getRequestToken().then(token => {
        this.$store.dispatch('getFiatCountryList', token).then(res => {
          this.countryList = res.data;
        }).catch(e => {
          console.error(e);
          return;
        });
      });
    },
    sortBankList() {
      this.bankList.sort((a, b) => {
        if (a.name < b.name) {
          return -1;
        }
        else if (a.name > b.name) {
          return 1;
        }
        return 0;
      });
    },
    formatErrorMessage(error) {
      if (error.errorCode && error.errorCode.includes('required.openapi.validation')) {
        return {
          path: this.translateToHumanReadable(error.path.split('.').pop()),
          message: `is required, and missing in your submission.`,
        };
      }
      else if (error.errorCode && error.errorCode.includes('format.openapi.validation')) {
        return {
          path: this.translateToHumanReadable(error.path.split('.').pop()),
          message: error.message,
        };
      }
      else if (error.errorCode && error.errorCode.includes('enum.openapi.validation')) {
        return {
          path: this.translateToHumanReadable(error.path.split('.').pop()),
          message: error.message,
        };
      }
      else if (error.errorCode && error.errorCode.includes('invalid')) {
        return {
          path: this.translateToHumanReadable(error.errorCode.split('.').pop()),
          message: error.message,
        };
      }
      else {
        if (error.path) {
          return {
            path: this.translateToHumanReadable(error.path.split('.').pop()),
            message: `Error found on the ${error.path.split('.').pop()} field. ${error.message}`,
          };
        } else {
          return {
            path: '',
            message: error.message,
          };
        }
      }
    },
    translateToHumanReadable(str) {
      switch (str) {
        case 'bank_account_type':
          return 'Account type';
        case 'account_type':
          return 'Account format';
        default:
          return str.replace(/_/g, ' ').toUpperCase();
      }
    },
    getErrorAcceptedValues(error) {
      if (error.message) {
        const symbol = error.message.includes('Accepted values :') ? 'Accepted values :' : 'Accepted value';
        return error.message.split(symbol)[1];
      }
      return '';
    },
    scrollToTop() {
      window.scrollTo(0, 0);
    },

    /********************* SET & RESET ***********************/
    setDefaultValues() {
      if (!this.isBeneficiary) {
        this.corporateForm.registration.uboDocType = PAYOUT_DOC_TYPES.PASSPORT.value;
      }
    },

    getRequiredFieldsList() {
      this.getRequestToken().then(token => {
        this.$store.dispatch('getFiatAccountRequiredFields', { token, country: this.accountForm.country.code, currency: this.accountForm.currency.currency, network: `network:${this.accountForm.currency.network}` }).then(res => {
          this.accountForm.requiredFields = res.data[this.accountForm.country.code]['bank_account'];
        }).catch(e => {
          console.error(e.response, e.response.data.errors);
        });
      }).catch(e => {
        console.error(e.response, e.response.data.errors);
      });
    },

    setCurrencyList() {
      this.accountForm.currencyList = [];
      if (this.accountForm.country && this.accountForm.country.services && this.accountForm.country.services.bank_account) {
        for (let cur of this.accountForm.country.services.bank_account) {
          // Ignore null currencies and tags other than 'network:'
          if (cur.currency && cur.network && cur.network.toLowerCase().includes('network')) {
            // need to deep copy, to avoid reference issues : having currencies that are already cleaned to not pass the IF
            const currency = JSON.parse(JSON.stringify(cur));
            currency.network = cur.network.replace('network:', '');
            this.accountForm.currencyList.push(currency);
          }
        }
      }
    },

    setEnabledCountryList() {
      this.getRequestToken().then(token => {
        this.$store.dispatch('getFiatCountryList', token).then(res => {
          this.accountForm.enabledCountryList = this.filterCountryList(res.data);
          this.setCurrencyList();
        }).catch(e => {
          console.error(e);
          return;
        });
      });
    },

    setAccountName() {
      if (this.accountForm.type.value === PAYOUT_ACCOUNT_TYPES.INDIVIDUAL.value) {
        try {
          this.accountForm.account_name = `${this.accountForm.inputs.first_name.value} ${this.accountForm.inputs.last_name.value}`;
        } catch (e) {
          this.accountForm.account_name = '';
          this.accountForm.errors.first_name = true;
          this.accountForm.errors.last_name = true;
        }
      } else if (this.accountForm.type.value === PAYOUT_ACCOUNT_TYPES.CORPORATE.value) {
        try {
          this.accountForm.account_name = `${this.accountForm.inputs.registered_name.value}`;
        } catch (e) {
          this.accountForm.account_name = '';
          this.accountForm.errors.registered_name = true;
        }
      } else {
        this.accountForm.account_name = '';
      }
    },

    getFiatMerchantConfig() {
      this.getRequestToken().then(token => {
        this.$store.dispatch('getMerchantFiatConfig', token).then(res => {
          this.merchantConfig = res.data;
        }).catch(e => {
          console.error(e);
          return;
        });
      });
    },

    async getRequestToken() {
      const merchantKey = this.$store.getters.activeMerchantKeyGetter;
      const response = await this.$store.dispatch('getMerchantPaymentRequestToken', merchantKey);
      this.requestToken = response.data.access_token;
      return response.data.access_token;
    },
    async setBankList(bankName = '') {
      if (!this.accountForm.country) {
        return;
      }

      this.isBankListLoading = true;
      this.getRequestToken().then(token => {
        const data = {
          'token': token,
          'country_code': this.accountForm.country.code,
          'name': bankName,
          'currency': this.accountForm.currency.currency,
          'network': `network:${this.accountForm.currency.network}`,
        };
        this.$store.dispatch('getFiatBankList', data).then(res => {
          this.isBankListLoading = false;
          this.bankList = res.data;
          this.sortBankList();
        });
      });
    },

    resetData() {
      this.accountForm = {
        type: PAYOUT_ACCOUNT_TYPES.CORPORATE,
        isActive: true,
        country: '',
        currency: '',
        inputs: {},
        errors: {},
        enabledCountryList: [],
        requiredFields: {},
      };
      this.shouldShowReview = false;
      this.hasErrors = false;
      this.createdAccount = {
        id: '',
      };
      this.resetMerchantConfig();
      this.resetIndividualForm();
      this.resetCorporateForm();
      this.resetPaymentDetailsForm();
      this.resetKycErrors();
      this.resetFlags();
      this.resetAllErrors();
      this.today = new Date().toISOString().split('T')[0];
    },
    resetIndividualFormError() {
      this.individualForm.errors.firstName = false;
      this.individualForm.errors.lastName = false;
      this.individualForm.errors.birthdate = false;
      this.individualForm.errors.gender = false;
      this.individualForm.errors.phone = false;
      this.individualForm.errors.residenceCountry = false;
      this.individualForm.errors.idType = false;
      this.individualForm.errors.idNumber = false;
      this.individualForm.errors.email = false;
      this.individualForm.errors.address = false;
      this.individualForm.errors.zip = false;
      this.individualForm.errors.city = false;
    },
    resetCorporateFormErrors() {
      this.corporateForm.errors.email = false;
      this.corporateForm.errors.nameRegistered = false;
      this.corporateForm.errors.incorporationCountry = false;
      this.corporateForm.errors.phone = false;
      this.corporateForm.errors.businessIncDoc = false;
      this.corporateForm.errors.address = false;
      this.corporateForm.errors.city = false;
      this.corporateForm.errors.zip = false;
      this.corporateForm.errors.country = false;
    },
    resetPaymentErrors() {
      this.paymentDetailsForm.errors.bank = false;
      this.paymentDetailsForm.errors.account_number = false;
      this.paymentDetailsForm.errors.routing_code = false;
      this.paymentDetailsForm.errors.routing_type = false;
      this.paymentDetailsForm.errors.account_type = false;
    },
    resetKycErrors() {
      this.kycForm.errors.kyc = false;
    },
    resetAllErrors() {
      this.resetPaymentErrors();
      this.hasErrors = false;
      this.creationError = [];
    },
    resetIndividualForm() {
      this.individualForm = {
        firstName: '',
        lastName: '',
        gender: '',
        birthdate: '',
        referenceId: '',
        email: '',
        phone: '',
        residenceCountry: '',
        address: '',
        zip: '',
        city: '',
        state: '',
        idIssuingCountry: '',
        nationality: '',
        idType: '',
        idNumber: '',
        idIssueDate: '',
        idExpDate: '',
        kycFileInput: null,
        isOrderToConfirm: false,
        occupation: '',
        errors: {
          firstName: false,
          lastname: false,
          birthdate: false,
          phone: false,
          residenceCountry: false,
          idType: false,
          idNumber: false,
          email: false,
          kyc: false,
          address: false,
          zip: false,
          city: false,
          state: false,
        }
      };
      this.paymentDetailsForm.accType = this.paymentAccountType.CHECKING;
      this.paymentDetailsForm.routingType = '';
      this.paymentDetailsForm.accFormat = this.paymentAccountFormats.ACC_NUM;
      this.bankList = [];
      this.shouldResetKycForm = true;
    },
    resetCorporateForm() {
      this.corporateForm = {
        internalRef: '',
        email: '',
        phone: '',
        registration: {
          nameRegistered: '',
          tradeName: '',
          licenseType: 'Trade license',
          registrationNumber: '',
          incorporationDate: '',
          incorporationCountry: '',
          uboDocType: PAYOUT_DOC_TYPES.PASSPORT.value,
          uboDoc: null,
          businessIncDoc: null,
          businessNature: '',
        },
        address: {
          country: undefined,
          city: '',
          state: '',
          zip: '',
          address: ''
        },
        errors: {
          email: false,
          phone: false,
          nameRegistered: false,
          country: false,
          zip: false,
          city: false,
          address: false,
        }
      };
    },
    resetPaymentDetailsForm() {
      this.paymentDetailsForm = {
        errors: {},
        inputs: {
          type: '',
          receiving_institution_id: '', // bank id
          currency: '', // bank currency
          country_code: '', // bank country
          bank_name: '',
          account: {
            account_number: '',
            account_type: '',
            bank_account_type: '',
            routing_type: '',
            routing_code: '',
          }
        },
      };
    },
    resetKycForm() {
      this.kycForm = {
        isOrderToConfirm: false,
        kycFileInput: null,
        errors: {
          kyc: false
        }
      };
    },
    resetFlags() {
      this.isCreationLoading = false;
      this.isCreationSuccessful = false;
      this.creationError = [];
      this.isBankListLoading = false;
    },
    resetMerchantConfig() {
      this.merchantConfig = {
        is_restrict_to_self: '',
        kyc_reliance: '',
        name: '',
        restricted_sender_ids: '',
        restricted_sender_type: '',
        whitelist_domains: '',
      };
    },
  },
  watch: {
    isCreationLoading(newVal) {
      const ID = '#account-creation-loader';
      if (newVal) {
        this.$vs.loading({
          container: ID,
          scale: 1,
          background: '#f8f8f8',
          text: 'Creating Account...',
        });
      }
      else {
        this.$vs.loading.close(ID + ' > .con-vs-loading');
      }
    },
    getIncorporationCountry() {
      this.corporateForm.address.country = this.corporateForm.registration.incorporationCountry;
    },
    activeMerchantKey() {
      this.initPage();
    },
    accountType() {
      this.resetAllErrors();
    },
    merchantConfig() {
      this.checkRights();
    },
    areAllParametersSelected(newVal) {
      if (newVal) {
        this.getRequiredFieldsList();
        this.setBankList();
      }
    },
    accountCountry(newVal) {
      if (newVal) {
        this.setCurrencyList();
        this.accountForm.currency = this.accountForm.currencyList.length ? this.accountForm.currencyList[0] : '';
      }
    },
    accountCurrency(newVal) {
      if (newVal && this.accountForm.country) {
        this.getRequiredFieldsList();
      }
    },

  },
  components: { FiatKycForm, FiatAccountFormFactory, DestinationAccountForm, FiatAccountReview }
};
</script>

<style lang="scss" scoped>
.fiat-account-creation-wrapper {

  form {
    max-width: 700px;

    fieldset {
      border: none;
      margin: $margin-xl 0;
      display: flex;
      flex-wrap: wrap;
      column-gap: 12px;
      row-gap: 6px;
      align-content: flex-start;


      legend {
        font-weight: bold;
        color: $color-primary-light;
      }

      p {
        font-weight: bold;
        margin-top: $margin-l;
        display: block;
        width: 100%;
      }

      label {
        position: relative;
        width: calc(50% - 6px);
        min-width: 200px;

        &>* {
          width: 100%;
        }

        &.small {
          width: 250px;
        }

        &.full {
          width: 100%;
        }

        &.three-quarter {
          width: 75%;
        }

        &>span.req {
          color: red;
          width: 1em;
          margin-left: 3px;
        }

        input[type="file"] {
          &::file-selector-button {
            background-color: $color-primary;
            color: $color-secondary;
            border-radius: 3px;
            border: none;
            text-align: center;
            font-size: 1rem;
            padding: 12px;
            margin-right: 8px;
            transition: 0.1s ease-in-out;
            // min-height: calc(24px + 8px + 8px);

            &:hover {
              background-color: $color-primary-dark;
              cursor: pointer;
            }
          }
        }
      }

      div.identification {
        width: 100%;
        display: flex;
        flex-wrap: wrap;
        column-gap: 12px;
        row-gap: 6px;
        align-content: flex-start;
      }

      div.company-documents {
        width: 100%;
        display: flex;
        flex-wrap: wrap;

        &>span {
          width: 100%;
          display: block;
        }

        &>label:first-of-type {
          width: min-content;
          min-width: 150px;
          margin-right: 12px;
        }
      }
    }
  }

  form {
    .banner {
      font-weight: normal;

      span {
        font-weight: bold;
      }

      a {
        text-decoration: underline;
      }
    }

  }

  fieldset.vital-infos {
    width: 800px;
    margin: $margin-s 0;

    label {
      &:first-of-type {
        width: 120px;
        min-width: 120px;
      }

      &:nth-of-type(2) {
        width: auto;
        flex-grow: 1;
      }

      &:last-of-type {
        width: 200px;
        min-width: 200px;
      }
    }

    p.banner {
      margin-bottom: $margin-s;
    }
  }

  .kyc-wrapper {
    display: block;
    width: 100%;
    margin-bottom: $margin-l;
    max-width: 650px;

    h3 {
      font-weight: bold;
      color: $color-primary-light;
    }

    &>p.error {
      margin-top: $margin-xs;
      font-weight: normal;
    }

    p.banner.info {
      font-weight: normal;
    }
  }

  .fiat-kyc-form {
    margin-top: $margin-m;
  }

  .vs-select--options {
    text-transform: capitalize;
  }

  .tooltip {
    right: 0;
    border-radius: 5px;
    margin-left: 3px;

    img {
      height: 16px;
      width: 16px;
      position: relative;
      top: 3px;
    }

    .tooltip-text {
      z-index: 1;
      visibility: hidden;
      position: absolute;
      background: white;
      padding: 10px;
      border-radius: 3px;
      font-size: 12px;
      max-width: 550px;
      min-width: 150px;
      white-space: normal;
      font-weight: normal;
      top: -16px;
      color: $color-text;
      box-shadow: rgba(60, 64, 67, 0.3) 0px 1px 2px 0px, rgba(60, 64, 67, 0.15) 0px 2px 6px 2px;
    }

    &:hover {
      .tooltip-text {
        visibility: visible;
      }
    }
  }

  button.btn,
  a {
    &.confirm {
      background-color: $color-success;
      width: 200px;

      &:hover {
        background-color: $color-success-darker;
      }

      &:disabled {
        background-color: lightgrey;
      }
    }

    &.back {
      margin-right: 0;
      color: $color-text;
      background: transparent;
      border: none;
      text-align: center;
      min-height: calc(24px + 8px + 8px);
      width: fit-content;
      margin-bottom: $margin-s;
      padding-right: 8px;

      &>svg {
        top: 6px;
        position: relative;
      }

      &::before {
        left: 4px;
      }

      &:hover {
        text-decoration: underline;
      }
    }
  }

  .error {
    color: $color-error-darker;
  }

  .banner {
    margin-bottom: $margin-l;
    padding: $margin-m;
    border-radius: 5px;
    margin-top: $margin-m;

    &.errors {
      li {
        margin-bottom: $margin-s;
      }
    }
  }

  #bank-list-loading {
    height: 24px;
    width: 24px;
    position: absolute;
    right: 0px;
    top: 28px;
    background-color: transparent;
    margin-right: 6px;

    &:hover {
      cursor: pointer;
    }

    &[disabled] {
      background-color: #f8f8f8;

      &:hover {
        cursor: default;
      }
    }

    &>.con-vs-loading {
      height: 24px;
      width: 24px;
      position: relative;
    }
  }
}
</style>